/**
 * Helpers for DICOM Encapsulated PDF instances (SOP Class
 * 1.2.840.10008.5.1.4.1.1.104.1). These instances carry a PDF blob in
 * tag (0042,0011) EncapsulatedDocument instead of pixel data, so they
 * can't be fed to Cornerstone's image rendering pipeline. We detect
 * them up-front, extract the PDF, and hand it to the browser's
 * built-in PDF viewer via <iframe>.
 */
import dicomParser from 'dicom-parser';
import { SeriesData } from '../cornerstone/init';

/** SOP Class UIDs for the family of DICOM "encapsulated document"
 *  storages. We only render PDF in this release; the others (CDA, STL,
 *  OBJ, MTL) would each need their own renderer. */
export const ENCAPSULATED_PDF_SOP_CLASS = '1.2.840.10008.5.1.4.1.1.104.1';

/** Tag identifier dicom-parser uses for EncapsulatedDocument. */
const TAG_ENCAPSULATED_DOCUMENT = 'x00420011';

/**
 * Heuristic for "this series is a DICOM PDF report" — works across
 * EDGEPACS and DICOMweb.
 *
 * Primary signal: Modality === 'DOC' (DICOM modality code for
 * documents; both backends report this).
 * Fallback: a single-image series whose seriesDescription hints at a
 * PDF report (some PACSes don't fill Modality correctly).
 */
export function isPdfSeries(series: SeriesData | undefined | null): boolean {
  if (!series) return false;
  if ((series.modality || '').toUpperCase() === 'DOC') return true;
  return false;
}

/**
 * Parse the DICOM bytes returned by either backend, pull the
 * EncapsulatedDocument blob out, and wrap it as a `Blob` typed as
 * application/pdf so URL.createObjectURL produces a URL Chrome / Edge
 * / Safari render natively in an iframe.
 *
 * Throws on parse failure or if the file doesn't actually carry a
 * PDF blob. Caller is responsible for revoking the blob URL when the
 * viewport unloads to avoid a leak.
 */
export function extractPdfBlobFromDicom(buffer: ArrayBuffer): Blob {
  const byteArray = new Uint8Array(buffer);
  const dataSet = dicomParser.parseDicom(byteArray);
  const sopClass = dataSet.string('x00080016') || '(unknown)';
  const element = dataSet.elements[TAG_ENCAPSULATED_DOCUMENT];
  if (!element) {
    // Helpful diagnostic — surfaces in the console so we can tell at a
    // glance whether the file was actually an Encapsulated PDF SOP that
    // happens to lack the tag, OR some other SOP class entirely.
    console.warn(
      `[PDF] DICOM file has no EncapsulatedDocument (0042,0011). SOPClassUID=${sopClass}`
    );
    throw new Error(`No EncapsulatedDocument tag (SOPClassUID: ${sopClass}).`);
  }
  const pdfBytes = byteArray.subarray(
    element.dataOffset,
    element.dataOffset + element.length
  );
  console.log(
    `[PDF] Extracted ${pdfBytes.length.toLocaleString()} bytes from EncapsulatedDocument. SOPClassUID=${sopClass}`
  );
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

/** Modality codes commonly used for actual image-bearing instances.
 *  Anything outside this list triggers a "peek the file" detection step
 *  even for multi-instance series, because some PACS report PDF reports
 *  with the original modality of the patient's exam. */
const IMAGING_MODALITIES = new Set([
  'CT', 'MR', 'CR', 'DX', 'US', 'XA', 'MG', 'PT', 'NM',
  'RF', 'IO', 'XC', 'PX', 'ES', 'OP', 'OPV', 'EPS', 'OCT',
  'BI', 'DG', 'IVUS', 'AR',
]);

/** Should we attempt the (expensive) peek-and-parse to look for a PDF?
 *  Yes when: modality is empty/unknown OR series has at most one instance
 *  (PDFs are always single-instance per SOP spec). */
export function shouldPeekForPdf(series: SeriesData | undefined | null): boolean {
  if (!series) return false;
  const mod = (series.modality || '').toUpperCase().trim();
  if (mod === '') return true;
  if (!IMAGING_MODALITIES.has(mod)) return true;
  return (series.numImages || 0) <= 1;
}
